import { logEvent } from "@shared/analytics/analytics";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { GenericTable } from "@shared/components/GenericTable";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { TableAction, TableColumn } from "@shared/types/table";
import { toast } from "@shared/ui/use-toast";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDeleteNetwork, useNetworkList } from "../hooks";
import { NetworksFilterPanel } from "./FilterPanel";

// Network interface
interface Network {
  id: string;
  networkId: string;
  name: string;
  type: string;
  status: string;
  currentCapacity: string;
  designCapacity: number;
  customerConnections: number;
  zones: string;
  createdBy: string;
  createdOn: string;
}

interface NetworkFilters {
  type?: string;
  status?: string;
}

export const NetworksTable: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const remoteUtilityId = getRemoteUtilityId();

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [networkToDelete, setNetworkToDelete] = useState<any>(null);

  // Extract current params from URL
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentType = searchParams.get("type") || "";
  const currentStatus = searchParams.get("status") || "";

  // Update current page from URL
  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get("page") || "1");
    setCurrentPage(pageFromUrl);
  }, [searchParams]);

  useEffect(() => {
    logEvent("Networks Table Viewed", {
      timestamp: new Date().toISOString(),
    });
  }, []);

  const { mutate: deleteNetworkMutation, isPending: isDeletingNetwork } =
    useDeleteNetwork();

  const handleConfirmDelete = () => {
    if (networkToDelete) {
      logEvent("Network Delete Confirmed", {
        networkId: networkToDelete.id,
      });
      deleteNetworkMutation(
        { networkId: networkToDelete.id },
        {
          onSuccess: () => {
            toast({
              title: "Success",
              description: `"${networkToDelete.name}" has been deleted successfully.`,
            });
            setShowDeleteDialog(false);
          },
          onError: (error) => {
            toast({
              title: "Delete Failed",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
    }
  };

  // Format filters for the hook - record-based approach like other components
  const formatFiltersForHook = () => {
    const hookFilters: Record<string, any> = {
      remote_utility_id: remoteUtilityId,
      page: currentPage,
      limit: PAGE_SIZE,
      is_pagination_required: 1,
    };

    // Add search term
    if (currentSearch.trim()) {
      hookFilters.search = currentSearch.trim();
    }

    // Add filters - only include non-empty values
    if (currentType && currentType !== "all") {
      hookFilters.type = currentType;
    }

    if (currentStatus && currentStatus !== "all") {
      hookFilters.status = currentStatus;
    }

    return hookFilters;
  };

  // Call API with formatted filters - using record-based hook
  const { data: networkData, isLoading } = useNetworkList(
    formatFiltersForHook()
  );

  // Current filters for NetworksFilterPanel component
  const currentFilters = useMemo(
    () => ({
      type: currentType,
      status: currentStatus,
    }),
    [currentType, currentStatus]
  );

  // Transform API response to match Network interface
  const transformedNetworks: Network[] = useMemo(() => {
    if (!networkData?.results) return [];

    return networkData.results.map((item: any) => ({
      id: item.id,
      networkId: item.networkNo,
      name: item.networkName,
      type: item.networkTypeDisplay,
      status: item.operationalStatusDisplay,
      currentCapacity: item.currentThroughput ?? 0,
      designCapacity: item.designCapacity,
      customerConnections: item.customerConnections,
      zones:
        Array.isArray(item.zonesDisplay) && item.zonesDisplay.length > 0
          ? item.zonesDisplay.join(" | ")
          : "N/A",
      createdBy: item.createdUserRemoteName,
      createdOn: item.createdDate,
    }));
  }, [networkData]);

  // Calculate pagination info - now from hook response
  const totalItems = networkData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    }),
    [currentPage, totalPages, totalItems, startItem, endItem]
  );

  // Helper functions for badge colors and text
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
      case "In Service":
        return "bg-green-100 text-green-800";
      case "Maintenance":
      case "Under Maintenance":
        return "bg-blue-100 text-blue-800";
      case "Standby":
        return "bg-yellow-100 text-yellow-800";
      case "Inactive":
      case "Out of Service":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "distribution":
      case "Distribution":
        return "bg-blue-100 text-blue-800";
      case "transmission":
      case "Transmission":
        return "bg-purple-100 text-purple-800";
      case "service":
      case "Service":
        return "bg-cyan-100 text-cyan-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleDeleteClick = (network: any) => {
    setNetworkToDelete(network);
    setShowDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setNetworkToDelete(null);
  };

  // Handle apply filters - similar to other components
  const handleApplyFilters = (newFilters: NetworkFilters) => {
    logEvent("Networks Filters Applied", { filters: newFilters });
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    // Handle type filter
    if (newFilters.type && newFilters.type !== "all") {
      newParams.type = newFilters.type;
    } else {
      delete newParams.type;
    }

    // Handle status filter
    if (newFilters.status && newFilters.status !== "all") {
      newParams.status = newFilters.status;
    } else {
      delete newParams.status;
    }

    // Reset to page 1 when filters change
    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    logEvent("Networks Filters Reset", {});
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    // Clear all filter parameters
    delete newParams.type;
    delete newParams.status;
    delete newParams.search;

    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Handle search - similar to other components
  const handleSearch = (searchTerm: string) => {
    logEvent("Networks Search Performed", { query: searchTerm });
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }

    newParams.page = "1";
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    logEvent("Networks Page Changed", { page });
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  // Table columns configuration
  const columns: TableColumn<any>[] = [
    {
      key: "networkId",
      header: "Network ID",
      type: "text",
      headerTooltip: "The unique identifier for this network.",
    },
    {
      key: "name",
      header: "Network Name",
      type: "text",
      headerTooltip: "The common name used to identify this network.",
    },
    {
      key: "type",
      header: "Network Type",
      type: "badge",
      badgeConfig: {
        getClassName: (value: string) => getTypeColor(value),
        variant: "secondary",
      },
      headerTooltip:
        "The classification of this network (e.g., Distribution, Transmission, Service).",
    },
    {
      key: "zones",
      header: "Zones/Areas",
      type: "custom",
      render: (value) => <HoverRevealText text={value} />,
      headerTooltip:
        "The geographic zones and areas that receive water from this network.",
    },
    {
      key: "customerConnections",
      header: "Customer Connections",
      type: "custom",
      render: (value: number) => value?.toLocaleString() || "0",
      headerTooltip:
        "The total number of customer connections served by this network. This is a key indicator of the network's impact.",
    },
    {
      key: "designCapacity",
      header: "Design Capacity",
      type: "text",
      headerTooltip: "The maximum design capacity of the network.",
    },
    {
      key: "currentCapacity",
      header: "Current Capacity",
      type: "text",
      headerTooltip: "The facility's current operational capacity.",
    },
    {
      key: "status",
      header: "Operational Status",
      type: "badge",
      badgeConfig: {
        getClassName: (value: string) => getStatusColor(value),
        variant: "secondary",
      },
      headerTooltip: "The current operational status of the network.",
    },
    {
      key: "createdBy",
      header: "Created By",
      type: "text",
      headerTooltip: "The user who originally registered this network.",
    },
    {
      key: "createdOn",
      header: "Created On",
      type: "text",
      headerTooltip:
        "The date this network was first entered into the registry.",
    },
  ];

  // Table actions
  const actions: TableAction<Network>[] = [
    {
      label: "View Details",
      onClick: (row) => {
        navigate(`/ax/asset-registry/networks/view/${row.id}`);
        logEvent("Network Viewed", { id: row.id });
      },
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Edit",
      onClick: (row) => {
        navigate(`/ax/asset-registry/networks/create/edit/${row.id}`);
        logEvent("Network Edit Clicked", { id: row.id });
      },
      icon: "Pen",
      variant: "outline",
    },
    {
      label: "Delete",
      onClick: (item) => handleDeleteClick(item),
    },
  ];

  const hasActiveFilters = currentType || currentStatus || currentSearch;

  return (
    <div className="space-y-4">
      <GenericTable
        data={transformedNetworks || []}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search networks by Name, Id"
        searchParamName="search"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        actions={actions}
        onExport={() => {}}
        actionsType="dropdown"
        emptyMessage="No networks found"
        filterPanel={
          <NetworksFilterPanel
            currentFilters={currentFilters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Filters active
              </span>
            </div>
          ) : null
        }
      />

      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        title="Delete Network"
        itemName={networkToDelete?.name}
        description={`Are you sure you want to delete the network "${networkToDelete?.name}" (ID: ${networkToDelete?.networkId})? This will permanently remove the network and all its associated data. This action cannot be undone.`}
        isLoading={isDeletingNetwork}
      />
    </div>
  );
};
